import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import axiosInstance from "../../utils/axios";

export enum SubscriptionStatus {
  TRIALING = "trialing",
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  SUSPENDED = "suspended",
  EXPIRED = "expired",
}

export interface Subscription {
  plan: any;
  id: number;
  status: SubscriptionStatus;
  trialStart?: string | null;
  trialEnd?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  createdAt: string;
  updatedAt: string;
  associationId: number;
  planId: number;
}

interface SubscriptionState {
  subscriptions: Subscription[];
  subscription?: Subscription;
  status: "idle" | "loading" | "failed";
}

const initialState: SubscriptionState = {
  subscriptions: [],
  subscription: undefined,
  status: "idle",
};

export const fetchSubscriptionsByAssociation = createAsyncThunk(
  "subscription/fetchSubscriptionsByAssociation",
  async (associationId: number) => {
    const response = await axiosInstance.get(
      `/subscription/association/${associationId}`
    );
    return response.data;
  }
);

export const fetchSubscriptionById = createAsyncThunk(
  "subscription/fetchSubscriptionById",
  async (id: number) => {
    const response = await axiosInstance.get(`/subscription/${id}`);
    return response.data;
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setSubscriptions(state, action) {
      state.subscriptions = action.payload;
    },
    setSubscription(state, action) {
      state.subscription = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionsByAssociation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSubscriptionsByAssociation.fulfilled, (state, action) => {
        state.status = "idle";
        state.subscriptions = action.payload;
      })
      .addCase(fetchSubscriptionsByAssociation.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchSubscriptionById.fulfilled, (state, action) => {
        state.subscription = action.payload;
      });
  },
});

export function createSubscription(values: Partial<Subscription>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(`/subscription`, values);
    dispatch(fetchSubscriptionsByAssociation(values.associationId!));
    return response.data;
  };
}

export function updateSubscription(id: number, values: Partial<Subscription>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.put(`/subscription/${id}`, values);
    dispatch(fetchSubscriptionsByAssociation(values.associationId!));
    return response.data;
  };
}

export function deleteSubscription(id: number, associationId: number) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.delete(`/subscription/${id}`);
    dispatch(fetchSubscriptionsByAssociation(associationId));
  };
}

export const { setSubscriptions, setSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
